import { card, type CardId } from '@rummy/engine';
import type { PublicEvent, TableView } from '@rummy/protocol';
import { useMemo, useState } from 'react';
import { useI18n } from '../i18n/index.tsx';

const SUITS = [
  ['S', '♠'],
  ['H', '♥'],
  ['D', '♦'],
  ['C', '♣'],
] as const;
const RANKS = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];

type IntelEvent = Extract<PublicEvent, { type: 'drew' | 'discarded' }>;

export interface TableIntelProps {
  table: TableView;
  onClose: () => void;
}

/**
 * A memory aid, not an oracle. It records cards that the player has already
 * seen (their own hand, the wild card and public-table events) and makes the
 * two-deck duplicate count explicit. No unseen stock identity crosses this
 * component's boundary.
 */
export function TableIntel({ table, onClose }: TableIntelProps) {
  const { t, cardIndex } = useI18n();
  const [tab, setTab] = useState<'ledger' | 'moves'>('ledger');
  const history = table.publicHistory ?? table.events;
  const cards = useMemo(() => seenCards(table, history), [table, history]);
  const moves = useMemo(
    () => history.filter((event): event is IntelEvent => event.type === 'drew' || event.type === 'discarded').slice(-12).reverse(),
    [history],
  );
  const nameOf = (id: string) => table.seats.find((seat) => seat.id === id)?.handle ?? t('table.someone');

  return (
    <aside className="table-intel" aria-label={t('table.intel')}>
      <header>
        <div>
          <span className="table-intel__eyebrow">✦</span>
          <strong>{t('table.intel')}</strong>
        </div>
        <button type="button" onClick={onClose} aria-label={t('settings.close')}>×</button>
      </header>
      <div className="table-intel__tabs" role="tablist" aria-label={t('table.intel')}>
        <button type="button" role="tab" aria-selected={tab === 'ledger'} onClick={() => setTab('ledger')}>
          {t('table.intel.ledger')}
        </button>
        <button type="button" role="tab" aria-selected={tab === 'moves'} onClick={() => setTab('moves')}>
          {t('table.intel.moves')}
        </button>
      </div>

      {tab === 'ledger' ? (
        <div className="table-intel__ledger" role="tabpanel">
          {SUITS.map(([suit, glyph]) => (
            <div key={suit} className={`intel-suit intel-suit--${suit}`}>
              <span className="intel-suit__mark" aria-hidden="true">{glyph}</span>
              <div className="intel-suit__ranks">
                {RANKS.map((rank) => {
                  const count = cards.get(`${rank}:${suit}`)?.size ?? 0;
                  return (
                    <span key={rank} className={`intel-rank${count > 0 ? ' is-seen' : ''}`} title={count ? `${count} seen` : undefined}>
                      {cardIndex(rank)}{count > 1 && <i>{count}</i>}
                    </span>
                  );
                })}
              </div>
            </div>
          ))}
          {(cards.get('JOKER')?.size ?? 0) > 0 && (
            <p className="table-intel__jokers">★ × {cards.get('JOKER')!.size}</p>
          )}
          <p className="table-intel__note">{t('table.intel.empty')}</p>
        </div>
      ) : (
        <ol className="table-intel__moves" role="tabpanel">
          {moves.length === 0 ? <li className="table-intel__note">{t('table.highlights.empty')}</li> : moves.map((event, i) => (
            <li key={`${event.type}-${event.player}-${i}`}>
              <strong>{nameOf(event.player)}</strong>
              {event.type === 'drew'
                ? <span>{event.source === 'discard' ? t('table.intel.open') : t('table.intel.closed')}</span>
                : <span>{t('table.intel.threw', { card: shortCard(event.card, cardIndex) })}</span>}
            </li>
          ))}
        </ol>
      )}
    </aside>
  );
}

function seenCards(table: TableView, history: readonly PublicEvent[]): Map<string, Set<CardId>> {
  const seen = new Map<string, Set<CardId>>();
  const add = (id: CardId | null | undefined) => {
    if (!id) return;
    const parsed = card(id);
    const key = parsed.printedJoker ? 'JOKER' : `${parsed.rank}:${parsed.suit}`;
    const ids = seen.get(key) ?? new Set<CardId>();
    ids.add(id);
    seen.set(key, ids);
  };
  add(table.wildCard);
  add(table.discardTop);
  for (const id of table.you?.hand ?? []) add(id);
  for (const event of history) {
    if (event.type === 'discarded') add(event.card);
    if (event.type === 'drew' && event.source === 'discard') add(event.card);
  }
  return seen;
}

function shortCard(id: CardId, cardIndex: (rank: string) => string): string {
  const parsed = card(id);
  if (parsed.printedJoker) return '★';
  const glyph = ({ S: '♠', H: '♥', D: '♦', C: '♣' } as const)[parsed.suit ?? 'S'];
  return `${cardIndex(parsed.rank ?? '')}${glyph}`;
}

export type PublicSeatMove =
  | { kind: 'open' }
  | { kind: 'discard'; card: CardId }
  | null;

export function latestPublicMove(history: readonly PublicEvent[], player: string): PublicSeatMove {
  for (let i = history.length - 1; i >= 0; i--) {
    const event = history[i];
    if (!event || !('player' in event) || event.player !== player) continue;
    if (event.type === 'drew' && event.source === 'discard') return { kind: 'open' };
    if (event.type === 'discarded') return { kind: 'discard', card: event.card };
  }
  return null;
}

export function publicMoveLabel(move: PublicSeatMove): string | null {
  if (!move) return null;
  if (move.kind === 'open') return 'OPEN';
  const parsed = card(move.card);
  if (parsed.printedJoker) return '★';
  const glyph = ({ S: '♠', H: '♥', D: '♦', C: '♣' } as const)[parsed.suit ?? 'S'];
  return `${parsed.rank}${glyph}`;
}
